import { InvestorDetailsHouseholdNotesQuery } from '@/__generated__/graphql';
import { EditIcon } from '@/assets/icons/edit';
import { ArrowDown } from '@/assets/icons/info-icons/arrowDown';
import { CloseInfoIcon } from '@/assets/icons/info-icons/closeInfo';
import { NotesIcon } from '@/assets/icons/menu-icons/notes';
import { AddButtonIcon } from '@/assets/static/icons/add-button-icon';
import { DeleteItemIcon } from '@/assets/static/icons/delete-item';
import { ArrowUp } from '@/assets/static/icons/todo-page/arrow-up';
import Button from '@/components/basicComponents/button';
import { useResponsive } from '@/hooks/use-responsive';
import { useClickOutside } from '@/hooks/useClickOutside';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import { format } from 'date-fns';
import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { InvestorDetailsEntityTypeEnum } from '../types';

interface NotesPaneProps {
  entityType: InvestorDetailsEntityTypeEnum;
  legalEntityNotes: InvestorDetailsHouseholdNotesQuery['getInvestor']['legalEntityNotes'];
  aboutInvestorNotes: { id: string; entityName: string; note: string }[];
  isOpen: boolean;
  isModalWindowOpen: boolean;
  handleEntitySelect: (entityId: string) => void;
  handleCloseNotesPane: () => void;
  handleModalWindow: ({ type, note }: { type: string; note?: { legalEntityId: string; date: string; note: string } | null }) => void;
}

const NotesPane = ({
  entityType,
  legalEntityNotes,
  aboutInvestorNotes,
  isOpen,
  isModalWindowOpen,
  handleEntitySelect,
  handleCloseNotesPane,
  handleModalWindow
}: NotesPaneProps) => {
  const { isMobile, isTablet } = useResponsive();
  const ref = useRef<HTMLDivElement | null>(null);

  const [showAboutInvestorNotes, setShowAboutInvestorNotes] = useState(true);

  useClickOutside<HTMLDivElement>(ref, isOpen && !isModalWindowOpen ? () => handleCloseNotesPane() : () => null);

  return isMobile ? (
    <></>
  ) : (
    <NotesPaneWrapper id="notes" ref={ref} isOpen={isOpen} isTablet={isTablet}>
      <NotesTitleWrap id="notes">
        <TitleWrap id="notes">
          <NotesIcon id="notes" fill={themeVar('font.strong')} />
          <Title id="notes">Notes</Title>
        </TitleWrap>
        <CloseBtn onClick={handleCloseNotesPane}>
          <CloseInfoIcon fill={themeVar('action.primary')} width={14} height={14} />
        </CloseBtn>
      </NotesTitleWrap>
      <NotesBody id="notes">
        {aboutInvestorNotes?.length > 0 && (
          <>
            <NotesHeader cursor="pointer" onClick={() => setShowAboutInvestorNotes(!showAboutInvestorNotes)}>
              About Investor
              {showAboutInvestorNotes ? (
                <ArrowUp width={23} height={23} fill={themeVar('font.action')} />
              ) : (
                <ArrowDown style={{ position: 'relative', top: '1px', right: '4px' }} width={12} height={12} fill={themeVar('font.action')} />
              )}
            </NotesHeader>
            <NotesWrapper showAboutInvestorNotes={showAboutInvestorNotes}>
              <ExpandableWrapp>
                {aboutInvestorNotes.map((aboutInvestorNoteItem) => (
                  <React.Fragment key={aboutInvestorNoteItem.id}>
                    <NoteItem id="notes">
                      <NoteItemTitleWrap>
                        <NoteItemTitle
                          id="notes"
                          withRedirect={entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group}
                          onClick={() =>
                            entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group
                              ? handleEntitySelect(aboutInvestorNoteItem.id)
                              : null
                          }
                        >
                          {aboutInvestorNoteItem.entityName}
                        </NoteItemTitle>
                      </NoteItemTitleWrap>
                      <NoteItemDesc id="notes">{aboutInvestorNoteItem.note}</NoteItemDesc>
                    </NoteItem>
                  </React.Fragment>
                ))}
              </ExpandableWrapp>
            </NotesWrapper>
          </>
        )}
        <NotesHeader>Notes</NotesHeader>
        {legalEntityNotes.length > 0 &&
          legalEntityNotes.map(({ legalEntityId, name, notes }) => {
            return notes.map(({ date, note }) => (
              <React.Fragment key={guid()}>
                <NoteItem id="notes" withHover={true}>
                  <NoteItemTitleWrap>
                    <NoteItemTitle
                      id="notes"
                      withRedirect={entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group}
                      onClick={() =>
                        entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group
                          ? handleEntitySelect(legalEntityId)
                          : null
                      }
                    >
                      {name}
                    </NoteItemTitle>
                    <ActionIconsWrap id="noteActionButtons">
                      <EditIcon
                        fill={themeVar('action.primary')}
                        onClick={() => handleModalWindow({ type: 'edit-note', note: { legalEntityId, date, note } })}
                      />
                      <DeleteItemIcon
                        fill={themeVar('action.primary')}
                        width={28}
                        height={28}
                        onClick={() => handleModalWindow({ type: 'delete-note', note: { legalEntityId, date, note } })}
                      />
                    </ActionIconsWrap>
                  </NoteItemTitleWrap>
                  <NoteItemDate id="notes">{date ? format(new Date(date), 'LLL d, yyyy') : 'No date'}</NoteItemDate>
                  <NoteItemDesc id="notes">{note}</NoteItemDesc>
                </NoteItem>
                <Divider />
              </React.Fragment>
            ));
          })}
        <AddButtonWrapper>
          <Button size="md" onClick={() => handleModalWindow({ type: 'edit-note' })} icon={<AddButtonIcon />}>
            Add New Note
          </Button>
        </AddButtonWrapper>
      </NotesBody>
    </NotesPaneWrapper>
  );
};

export default NotesPane;

const NotesPaneWrapper = styled.div<{ isOpen: boolean; isTablet: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${({ isOpen }) => (isOpen ? '20px 40px' : 0)};
  position: fixed;
  margin-right: -24px;
  top: ${({ isTablet }) => (isTablet ? '50px' : 0)};
  bottom: 0;
  right: 0;
  width: ${({ isOpen }) => (isOpen ? '600px' : 0)};
  height: 100vh;
  background: ${themeVar('layer.1')};
  z-index: 1000;
  box-shadow: -14px 0 12px -10px rgba(0, 0, 0, 0.15);
  transition:
    all 0.3s,
    height 1ms;
`;

const Divider = styled.div`
  width: 100%;
  min-height: 1px;
  background: ${themeVar('border.base')};
  margin: 5px 0;
`;

const AddButtonWrapper = styled.div`
  margin-top: 10px;
`;

const NotesHeader = styled.div<{ cursor?: string }>`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: ${themeVar('font.weak')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${({ cursor }) => cursor || 'default'};
  border-bottom: 1px solid ${themeVar('border.base')};
  padding: 10px 0;
  margin-bottom: 5px;
`;

const NotesWrapper = styled.div<{ showAboutInvestorNotes: boolean }>`
  display: grid;
  grid-template-rows: ${({ showAboutInvestorNotes }) => (showAboutInvestorNotes ? '1fr' : '0fr')};
  overflow: ${({ showAboutInvestorNotes }) => (showAboutInvestorNotes ? 'visible' : 'hidden')};
  transition: grid-template-rows 300ms;
`;

const ExpandableWrapp = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const NoteItemDesc = styled.div`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: ${themeVar('font.base')};
  margin-top: 10px;
`;

const NoteItemDate = styled.div`
  font-family: Blinker, serif;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: ${themeVar('font.weak')};
`;

const NoteItemTitle = styled.div<{ withRedirect?: boolean }>`
  font-family: Blinker, serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 18px;
  color: ${themeVar('font.base')};
  cursor: ${({ withRedirect }) => (withRedirect ? 'pointer' : 'default')};

  ${({ withRedirect }) =>
    withRedirect &&
    css`
      &:hover {
        color: ${themeVar('font.action')};
      }
    `}
`;

const NoteItem = styled.div<{ withHover?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  transition: 0.3s;
  padding: 20px;
  border-radius: 8px;

  ${({ withHover }) =>
    withHover &&
    css`
      &:hover {
        background: ${themeVar('layer.2')};
      }

      &:hover #noteActionButtons {
        opacity: 1;
      }
    `}
`;

const NotesBody = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 30px;
  overflow: auto;
  overscroll-behavior: none;

  &::-webkit-scrollbar {
    width: 5px;
    left: -100px;
  }

  &::-webkit-scrollbar-track {
    background: ${themeVar('layer.2')};
    border: 1px solid ${themeVar('border.base')};
    padding: 2px;
    border-radius: 10px;
    margin: 6px 0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${themeVar('layer.4')};
    background-clip: padding-box;
    border-radius: 10px;
  }
`;

const Title = styled.div`
  font-family: Blinker, serif;
  font-size: 28px;
  font-weight: 600;
  line-height: 39px;
  color: ${themeVar('font.strong')};
`;

const TitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const CloseBtn = styled.div`
  display: flex;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  border: 1px solid ${themeVar('action.primary')};
  background: ${themeVar('layer.base')};
`;

const NotesTitleWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NoteItemTitleWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionIconsWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  opacity: 0;
  cursor: pointer;
`;
